import React from 'react';
import { 
  Target,
  Mail,
  MessageSquare,
  Phone,
  Linkedin,
  Zap,
  DollarSign,
  Calendar,
  Tag,
  AlertCircle
} from 'lucide-react';
import { Button } from '../../../../components/ui/Button/Button';
import { Card } from '../../../../components/ui/Card';
import { Input } from '../../../../components/ui/Input';
import { cn } from '../../../../utils/cn';

interface CampaignBasicInfoProps {
  data: any;
  onChange: (field: string, value: any) => void;
  errors: Record<string, string>;
}

const campaignTypes = [
  { 
    id: 'email', 
    label: 'Email Campaign', 
    icon: Mail, 
    description: 'Traditional email outreach with tracking and automation',
    color: 'bg-blue-500'
  },
  { 
    id: 'linkedin', 
    label: 'LinkedIn Outreach', 
    icon: Linkedin, 
    description: 'Professional networking and connection requests',
    color: 'bg-indigo-500'
  },
  { 
    id: 'cold_call', 
    label: 'Cold Calling', 
    icon: Phone, 
    description: 'Direct phone outreach with call tracking',
    color: 'bg-green-500'
  },
  { 
    id: 'sms', 
    label: 'SMS Campaign', 
    icon: MessageSquare, 
    description: 'Text message outreach for mobile engagement',
    color: 'bg-purple-500'
  },
  { 
    id: 'multi_channel', 
    label: 'Multi-Channel', 
    icon: Zap, 
    description: 'Coordinated outreach across multiple channels',
    color: 'bg-gradient-to-r from-purple-500 to-pink-500'
  }
];

const priorities = [
  { id: 'low', label: 'Low', color: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300' },
  { id: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  { id: 'high', label: 'High', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  { id: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' }
];

const objectives = [
  'Lead Generation',
  'Customer Acquisition',
  'Brand Awareness',
  'Product Launch',
  'Event Promotion',
  'Customer Retention',
  'Partnership Development',
  'Market Research'
];

export const CampaignBasicInfo: React.FC<CampaignBasicInfoProps> = ({ data, onChange, errors }) => {
  const handleTagAdd = (tag: string) => {
    if (tag && !data.tags.includes(tag)) {
      onChange('tags', [...data.tags, tag]);
    }
  };

  const handleTagRemove = (tagToRemove: string) => {
    onChange('tags', data.tags.filter((tag: string) => tag !== tagToRemove));
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">
          Campaign Basic Information
        </h2>
        <p className="text-neutral-600 dark:text-neutral-400">
          Set up the fundamental details for your new campaign
        </p>
      </div>

      {/* Campaign Name & Description */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
          Campaign Details
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Campaign Name *
            </label>
            <Input
              value={data.name}
              onChange={(e) => onChange('name', e.target.value)}
              placeholder="Enter campaign name..."
              className={cn(errors.name && "border-red-500")}
            />
            {errors.name && (
              <div className="flex items-center mt-1 text-sm text-red-600 dark:text-red-400">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.name}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Description
            </label>
            <textarea
              value={data.description}
              onChange={(e) => onChange('description', e.target.value)}
              placeholder="Describe your campaign goals and strategy..."
              rows={3}
              className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>
      </Card>

      {/* Campaign Type */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
          Campaign Type
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {campaignTypes.map((type) => {
            const Icon = type.icon;
            const isSelected = data.type === type.id;
            
            return (
              <button
                key={type.id}
                onClick={() => onChange('type', type.id)}
                className={cn(
                  "p-4 border-2 rounded-lg text-left transition-all duration-200",
                  isSelected
                    ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                    : "border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600"
                )}
              >
                <div className="flex items-center space-x-3 mb-2">
                  <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", type.color)}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h4 className="font-medium text-neutral-900 dark:text-neutral-100">
                    {type.label}
                  </h4>
                </div>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  {type.description}
                </p>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Objective & Priority */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
            Campaign Objective
          </h3>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Primary Objective *
            </label>
            <select
              value={data.objective}
              onChange={(e) => onChange('objective', e.target.value)}
              className={cn(
                "w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent",
                errors.objective && "border-red-500"
              )}
            >
              <option value="">Select objective...</option>
              {objectives.map((objective) => (
                <option key={objective} value={objective}>
                  {objective}
                </option>
              ))}
            </select>
            {errors.objective && (
              <div className="flex items-center mt-1 text-sm text-red-600 dark:text-red-400">
                <AlertCircle className="w-4 h-4 mr-1" />
                {errors.objective}
              </div>
            )}
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
            Priority Level
          </h3>
          <div className="space-y-2">
            {priorities.map((priority) => (
              <button
                key={priority.id}
                onClick={() => onChange('priority', priority.id)}
                className={cn(
                  "w-full p-3 rounded-lg text-left transition-all duration-200 border-2",
                  data.priority === priority.id
                    ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                    : "border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600"
                )}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-neutral-900 dark:text-neutral-100">
                    {priority.label}
                  </span>
                  <span className={cn("px-2 py-1 rounded-full text-xs font-medium", priority.color)}>
                    {priority.label}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </Card>
      </div>

      {/* Budget & Timeline */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
            Budget
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Total Budget
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <Input
                  type="number"
                  value={data.budget}
                  onChange={(e) => onChange('budget', parseFloat(e.target.value) || 0)}
                  placeholder="0.00"
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Currency
              </label>
              <select
                value={data.currency}
                onChange={(e) => onChange('currency', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="USD">USD - US Dollar</option>
                <option value="EUR">EUR - Euro</option>
                <option value="GBP">GBP - British Pound</option>
                <option value="CAD">CAD - Canadian Dollar</option>
              </select>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
            Timeline
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Start Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <Input
                  type="date"
                  value={data.startDate}
                  onChange={(e) => onChange('startDate', e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                End Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <Input
                  type="date"
                  value={data.endDate}
                  onChange={(e) => onChange('endDate', e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Tags */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
          Tags
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Add Tags
            </label>
            <div className="flex space-x-2">
              <Input
                placeholder="Enter tag and press Enter..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const target = e.target as HTMLInputElement;
                    handleTagAdd(target.value.trim());
                    target.value = '';
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                onClick={(e) => {
                  const input = (e.target as HTMLElement).parentElement?.querySelector('input');
                  if (input) {
                    handleTagAdd(input.value.trim());
                    input.value = '';
                  }
                }}
              >
                <Tag className="w-4 h-4 mr-2" />
                Add
              </Button>
            </div>
          </div>
          
          {data.tags.length > 0 && (
            <div>
              <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Current Tags
              </p>
              <div className="flex flex-wrap gap-2">
                {data.tags.map((tag: string) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400"
                  >
                    {tag}
                    <button
                      onClick={() => handleTagRemove(tag)}
                      className="ml-2 text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}; 